import { Suspense } from 'react';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
import Loader from '@shared/components/Loader';
import { Button, Stack, Typography, Box, Paper } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import * as Sentry from '@sentry/react';
import { FormattedMessage } from 'react-intl';

const PageBoundary = ({ children }: { children: React.ReactNode }) => {
  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onError={(error, info) => {
        if (import.meta.env.VITE_ENVIRONMENT === 'DEV') {
          console.error('PageBoundary caught error:', error, info);
        }
        Sentry.captureException(error, {
          extra: {
            componentStack: info.componentStack,
          },
          tags: {
            errorBoundary: 'page_level',
          },
        });
      }}
    >
      <Suspense fallback={<Loader />}>{children}</Suspense>
    </ErrorBoundary>
  );
};

export default PageBoundary;

const ErrorFallback = ({ error, resetErrorBoundary }: FallbackProps) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        p: 3,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 500,
          width: '100%',
        }}
      >
        <Stack spacing={3} alignItems="center">
          <ErrorOutlineIcon sx={{ fontSize: 56, color: 'error.main' }} />

          <Typography variant="h6" fontWeight="bold" textAlign="center">
            <FormattedMessage id="pageBoundary.title" />
          </Typography>

          <Typography variant="body2" color="text.secondary" textAlign="center">
            <FormattedMessage id="pageBoundary.description" />
          </Typography>

          {import.meta.env.VITE_ENVIRONMENT === 'DEV' && error ? (
            <Paper
              sx={{
                p: 2,
                bgcolor: 'grey.100',
                width: '100%',
                overflow: 'auto',
                maxHeight: 150,
              }}
            >
              <Typography
                variant="caption"
                component="pre"
                sx={{
                  fontFamily: 'monospace',
                  fontSize: '0.7rem',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {JSON.stringify(error)}
              </Typography>
            </Paper>
          ) : null}

          <Stack direction="row" spacing={2} width="100%">
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={resetErrorBoundary}
              fullWidth
            >
              <FormattedMessage id="pageBoundary.retry" />
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                window.location.href = '/hairdresser';
              }}
              fullWidth
            >
              <FormattedMessage id="pageBoundary.home" />
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
};
